import { useState } from 'react';
import type { CheckinResponse } from '@ridehub/shared';
import { Button } from '../../components/Button.js';
import { useGeolocationContext } from '../../contexts/GeolocationContext.js';
import { apiClient, ApiError } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';

interface CheckinButtonProps {
  spotId: string;
  onCheckinSuccess?: (result: CheckinResponse) => void;
}

export function CheckinButton({ spotId, onCheckinSuccess }: CheckinButtonProps) {
  const { user } = useAuth();
  const { latitude, longitude } = useGeolocationContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleCheckin = async () => {
    if (!user) {
      setError('チェックインにはログインが必要です');
      return;
    }
    if (latitude === null || longitude === null) {
      setError('現在地を取得中です。しばらくお待ちください。');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.post<CheckinResponse>(
        '/checkins',
        { spotId, userLat: latitude, userLng: longitude },
        { requireAuth: true }
      );
      if (result) {
        setSuccess(true);
        onCheckinSuccess?.(result);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('チェックインに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Button
        onClick={handleCheckin}
        loading={loading}
        variant="ghost"
        size="lg"
        className={`w-full border-2 ${success ? 'border-gray-300 text-gray-500' : 'border-primary text-primary'}`}
      >
        {success ? (
          <>✓ チェックイン済み</>
        ) : (
          <>
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="flex-shrink-0"
            >
              <ellipse cx="12" cy="17.5" rx="5.5" ry="3" />
              <ellipse cx="5.5" cy="12" rx="2" ry="2.5" />
              <ellipse cx="9.5" cy="9" rx="2" ry="2.5" />
              <ellipse cx="14.5" cy="9" rx="2" ry="2.5" />
              <ellipse cx="18.5" cy="12" rx="2" ry="2.5" />
            </svg>
            チェックイン
          </>
        )}
      </Button>
      {error && <p className="mt-2 text-sm text-red-500 text-center">{error}</p>}
    </div>
  );
}
